import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import moment from 'moment-timezone';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * TimeZoneAutocomplete - Modern timezone picker with smart search
 *
 * Features:
 * - Single searchable field (no region dropdown needed)
 * - Shows UTC offset and common abbreviations
 * - Groups by region for better organization
 * - Shows current time in selected timezone
 * - Auto-detects user's timezone as default
 *
 * @param {Object} props
 * @param {string} props.value - Current timezone value (e.g., "America/New_York")
 * @param {Function} props.onChange - Callback when timezone changes (newValue) => void
 * @param {boolean} props.required - Whether field is required
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {boolean} props.error - Whether to show error state
 * @param {string} props.helperText - Helper text to display
 * @param {string} props.label - Label for the field
 */
function TimeZoneAutocomplete({
  value,
  onChange,
  required = false,
  disabled = false,
  error = false,
  helperText = '',
  label = 'Time Zone',
  variant = 'outlined',
  hiddenLabel = false,
  showCurrentTime = true,
}) {
  const { t } = useTranslation();

  // Prepare timezone options with additional metadata
  const timezoneOptions = useMemo(() => {
    const now = Date.now();
    return moment.tz.names().map(tz => {
      const zone = moment.tz.zone(tz);
      const offset = zone.utcOffset(now);
      const offsetHours = Math.floor(Math.abs(offset) / 60);
      const offsetMinutes = Math.abs(offset) % 60;
      const offsetSign = offset <= 0 ? '+' : '-';
      const offsetString = `UTC${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

      // Get region and city
      const parts = tz.split('/');
      const region = parts[0];
      const city = parts.slice(1).join('/').replace(/_/g, ' ');

      // Get abbreviation
      const abbr = zone.abbr(now);

      return {
        value: tz,
        label: `${city || tz}`,
        region,
        offset,
        offsetString,
        abbr,
        displayLabel: `${city || tz} (${offsetString} ${abbr})`,
        searchString: `${city} ${tz} ${offsetString} ${abbr} ${region}`.toLowerCase(),
      };
    });
  }, []);

  // Group options by region
  const groupedOptions = useMemo(() => {
    const groups = {};
    timezoneOptions.forEach(option => {
      if (!groups[option.region]) {
        groups[option.region] = [];
      }
      groups[option.region].push(option);
    });
    return groups;
  }, [timezoneOptions]);

  // Sort regions: Popular ones first, then alphabetically
  const sortedRegions = useMemo(() => {
    const popularRegions = ['America', 'Europe', 'Asia', 'Pacific'];
    const allRegions = Object.keys(groupedOptions);
    const popular = allRegions.filter(r => popularRegions.includes(r)).sort();
    const others = allRegions.filter(r => !popularRegions.includes(r)).sort();
    return [...popular, ...others];
  }, [groupedOptions]);

  // Find current option
  const currentOption = timezoneOptions.find(opt => opt.value === value) || null;

  // Get current time in selected timezone
  const currentTime = value ? moment.tz(Date.now(), value).format('LT') : '';

  const handleChange = (event, newValue) => {
    if (onChange) {
      onChange(newValue?.value || null);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        value={currentOption}
        onChange={handleChange}
        options={timezoneOptions}
        groupBy={option => option.region}
        getOptionLabel={option => option.displayLabel}
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        disabled={disabled}
        renderInput={params => (
          <TextField
            {...params}
            variant={variant}
            label={hiddenLabel ? undefined : label}
            hiddenLabel={hiddenLabel}
            required={required}
            error={error}
            helperText={helperText}
            size="small"
            fullWidth
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant="body2">{option.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.offsetString} • {option.abbr}
              </Typography>
            </Box>
          </li>
        )}
        renderGroup={params => (
          <li key={params.key}>
            <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover', fontWeight: 600, fontSize: '0.875rem' }}>
              {params.group}
            </Box>
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </li>
        )}
        filterOptions={(options, { inputValue }) => {
          if (!inputValue) return options;
          const searchLower = inputValue.toLowerCase();
          return options.filter(option => option.searchString.includes(searchLower));
        }}
      />
      {showCurrentTime && currentTime && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {t('general.currentTime')}: {currentTime}
        </Typography>
      )}
    </Box>
  );
}

export default TimeZoneAutocomplete;
